/**
 * Resolves the delivery-proof image-validation policy from its two server
 * tiers into the single config the native capture screen consumes.
 *
 * Tier 1 — global, `GET /cpdb/setup-5` → `appSettings.delivery_proof_validation`
 *          (Redux `state.configReducer.appSettings`). Authed, so the backend can
 *          already vary it per DB / per CP / per city. This is the incident lever.
 *
 * Tier 2 — per order, `getUserOrders` → `popup_config.image_validation`. Carries
 *          the actual checks and thresholds.
 *
 * Why both: `appSettings` is redux-persisted and its saga swallows fetch errors,
 * so if `/cpdb/setup-5` fails the last persisted value stays live indefinitely —
 * a global bypass flip may never reach the DB who most needs it. `popup_config`
 * rides `getUserOrders`, which must be succeeding for the DB to be on this
 * screen at all. Two levers, two delivery paths, and the LOOSER of the two wins.
 *
 * Pure function, no Redux import, so the truth table is directly unit-testable.
 */

export type ValidationMode = "OFF" | "SHADOW" | "WARN" | "BLOCK";

// Ordered weakest → strongest. `min()` over this ordering is what makes the
// global override able to loosen but never tighten.
const MODE_RANK: Record<ValidationMode, number> = {
  OFF: 0,
  SHADOW: 1,
  WARN: 2,
  BLOCK: 3,
};

const isMode = (value: unknown): value is ValidationMode =>
  typeof value === "string" && value in MODE_RANK;

// Thresholds are numbers; goods_present also carries `allow_labels: string[]`
// and `deny_labels: Record<string, string[]>`. The client passes the block
// through untouched, so the index signature stays open rather than enumerating
// every check's shape here.
export interface CheckConfig {
  enabled?: boolean;
  [option: string]: unknown;
}

export interface ImageValidationConfig {
  enabled?: boolean;
  mode?: ValidationMode;
  force_allow_after_attempts?: number;
  checks?: Record<string, CheckConfig>;
  polybag?: { enabled?: boolean };
  output_max_dimension?: number;
  output_quality?: number;
}

export interface GlobalValidationSettings {
  enabled?: boolean;
  mode_override?: ValidationMode | null;
  bypass_allowed?: boolean;
  min_app_version_code?: number;
}

export interface ResolvedValidationConfig {
  mode: ValidationMode;
  force_allow_after_attempts: number;
  checks: Record<string, CheckConfig>;
  polybag: { enabled: boolean };
  output_max_dimension?: number;
  output_quality?: number;
}

/** Every path to enabling this is a server flip; the binary ships inert. */
export const OFF_CONFIG: ResolvedValidationConfig = {
  mode: "OFF",
  force_allow_after_attempts: 0,
  checks: {},
  polybag: { enabled: false },
};

export const isValidationActive = (
  config: ResolvedValidationConfig | null | undefined,
): boolean => !!config && config.mode !== "OFF";

export const resolveValidationConfig = ({
  popupConfig,
  appSettings,
  versionCode,
}: {
  popupConfig?: { image_validation?: ImageValidationConfig | null } | null;
  appSettings?: { delivery_proof_validation?: GlobalValidationSettings | null } | null;
  versionCode?: number | null;
}): ResolvedValidationConfig => {
  const perOrder = popupConfig?.image_validation;
  const global = appSettings?.delivery_proof_validation;

  // No per-order block means this order never asked for validation.
  if (!perOrder || perOrder.enabled === false) return OFF_CONFIG;

  // Global master switch. Absent is treated as ON so a backend that has not yet
  // shipped the appSettings key does not silently disable a feature ops just
  // turned on per-order — the per-order block is itself opt-in.
  if (global?.enabled === false) return OFF_CONFIG;

  // CodePush ships JS ahead of the binary. A JS bundle that knows about
  // validation can land on an APK with no ProofCaptureModule in it, so the
  // backend can floor this by binary rather than by bundle.
  const minVersion = global?.min_app_version_code ?? 0;
  if (minVersion > 0 && (versionCode ?? 0) < minVersion) return OFF_CONFIG;

  const requested: ValidationMode = isMode(perOrder.mode) ? perOrder.mode : "OFF";
  const override = global?.mode_override;
  // `?? BLOCK` means "no ceiling" — the strongest rank, so min() leaves the
  // per-order mode untouched when no override is set.
  const ceiling: ValidationMode = isMode(override) ? override : "BLOCK";
  const mode: ValidationMode =
    MODE_RANK[requested] <= MODE_RANK[ceiling] ? requested : ceiling;

  if (mode === "OFF") return OFF_CONFIG;

  // The valve is what stops a device that physically cannot take a passing
  // photo from making an order undeliverable. Ops can close it, but only
  // deliberately.
  const bypassAllowed = global?.bypass_allowed !== false;
  const forceAllow = bypassAllowed
    ? Math.max(0, Number(perOrder.force_allow_after_attempts) || 0)
    : 0;

  return {
    mode,
    force_allow_after_attempts: forceAllow,
    checks: perOrder.checks ?? {},
    polybag: { enabled: perOrder.polybag?.enabled === true },
    ...(perOrder.output_max_dimension
      ? { output_max_dimension: perOrder.output_max_dimension }
      : {}),
    ...(perOrder.output_quality ? { output_quality: perOrder.output_quality } : {}),
  };
};
